import { Box, Link, useToken } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftMessage from '@/components/Atoms/FaceliftMessage';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation/FaceliftDialogWithExitConfirmation';
import FaceliftInvoiceViewPDF, {
	DemoInvoiceViewModal,
} from '@/components/Molecules/FaceliftInvoiceViewPDF';
import DataTable from '@/components/Organisms/DataTable';
import type { onFilterApplyProps } from '@/components/Organisms/DataTable/DataTable.types';
import DenialSupportForm from '@/components/Templates/DenialSupport/DenialSupportForm/DenialSupportForm';
import InvoiceSplitView from '@/components/Templates/InvoiceSplitView';
import {
	getInvoiceStatus,
	type INVOICE_TYPE,
	columns as invoiceColumns,
	statusesFilterOptions,
	statusesFilterOptionsForOpen,
	statusInvoices,
} from '@/components/Templates/Invoices/invoices';
import { useGetAccounts } from '@/hooks/service-hooks/accounts';
import { useGetProductClinicPort } from '@/hooks/service-hooks/common';
import {
	useGetInvoices,
	useGetInvoicesProducts,
} from '@/hooks/service-hooks/invoice';
import { useInvoicesTutorial } from '@/hooks/tutorialsHooks/useInvoicesTutorial';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useAppTour } from '@/hooks/useAppTour';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { useInvoiceCart } from '@/hooks/useInvoiceCart';
import { useInvoicePDFViewer } from '@/hooks/useInvoicePDFViewer';
import usePermission from '@/hooks/usePermission';
import { getInvoices } from '@/services/invoice';
import { useAuthStore } from '@/store/authStore';
import { useTourContext } from '@/store/tourContext';
import type { Invoice } from '@/types/invoice';
import type { FilterType } from '@/types/table';
import { trackCoreClick } from '@/utils/analytics';
import { formatDateToString, getCurrentDate } from '@/utils/dateUtils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { generateMockInvoiceData } from '@/utils/tutorials/mockData/invoicesMockData';

export type InvoicesUnifiedVariant = keyof typeof INVOICE_TYPE;

interface InvoicesUnifiedTableProps {
	variant: InvoicesUnifiedVariant;
}

/**
 * InvoicesUnifiedTable Component
 *
 * Unified component for displaying and managing invoices in a data table.
 * Supports three variants: 'all' for all invoices, 'paid' for paid invoices only,
 * and 'open' for open invoices with cart and permission features.
 *
 * Features:
 * - Fetches and displays invoice data with pagination
 * - Supports filtering by status, search, and global filters (clinics, products)
 * - Allows selection of invoices to add to cart (open variant)
 * - Supports export to CSV, Excel, and PDF
 * - Expandable rows for invoices with multiple products
 * - Single/multiple invoice PDF viewing
 */

const CLINIC_KEY = 'Account_ID';
const PRODUCT_KEY = 'QCode';
const QBO_FILTER_KEY = 'QBO_CLINIC_IDS';

const InvoicesUnifiedTable: React.FC<InvoicesUnifiedTableProps> = ({
	variant,
}) => {
	const [brand600] = useToken('colors', ['brand.600']);

	// Clinic filter hook
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);

	// Get authenticated user info
	const user = useAuthStore((state) => state.user?.userInformation);

	// Cart management hooks for 'open' variant
	const {
		addInvoiceToCart,
		selectedInvoices,
		cartedInvoices,
		removeInvoiceFromCart,
	} = useInvoiceCart();

	const { hasPermission } = usePermission();
	const ENABLE_PRODUCT_BY_PORT = ['mindsight', 'salesrep'].includes(
		(user?.portsAvailable || '').toLowerCase(),
	);

	// Tour context for tutorial detection
	const { isAnyInvoiceOrOnboardingTutorialActive: isAnyInvoiceTutorialActive } =
		useTutorialActive();

	// Fetch clinic accounts to get QBO IDs dynamically
	const { data: clinicsData, isFetching: isClinicsFetching } = useGetAccounts({
		page: 1,
		limit: 5000,
		enabled: defaultClinicIds.length > 0,
		ids: defaultClinicIds,
	});

	// State for managing table filters (pagination, search, sort, etc.)
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: false, // Initially disabled until clinicsData is fetched
		tab: variant, // Identifies the table mode (all/open/paid) for audit logging
		status:
			variant === 'open'
				? statusesFilterOptionsForOpen[0].value
				: variant === 'paid'
					? 'paid'
					: 'open',
		globalFilters: '[]', // Initially empty until clinicsData is fetched
		additionalFilters: '',
		sort: '',
	});

	const [products, setProducts] = useState<
		{ productName: string; qCode: string }[]
	>([]);

	const [denialSupportModal, setDenialSupportModal] = useState<{
		isOpen: boolean;
		isErrorOpen: boolean;
		message: string;
	}>({
		isOpen: false,
		isErrorOpen: false,
		message: '',
	});

	const [isDenialFormDirty, setIsDenialFormDirty] = useState(false);

	// Track if filters have been initialized to prevent infinite loops
	const [filtersInitialized, setFiltersInitialized] = useState(false);

	// Build a map of clinic GUID to QBO ID for dynamic lookup
	const clinicsQBOIds: Record<string, string> = useMemo(() => {
		if (!clinicsData?.items) return {};
		return clinicsData.items.reduce(
			(acc, clinic) => {
				acc[`{${clinic.accountid.toLowerCase()}}`] = clinic.ta_qbocustomerid;
				return acc;
			},
			{} as Record<string, string>,
		);
	}, [clinicsData]);

	// Fetch product-clinic port data for product filter options
	const { data: portProductData } =
		useGetProductClinicPort(
			{
				clinicIds: defaultClinicIds,
				port: user?.portsAvailable || '',
				enabled: defaultClinicIds.length > 0 && !!clinicsData?.items,
			},
			() => {
				// Callback will be handled in useEffect below
			},
		);

	// Set filters when clinicsData is fetched and portProductData is available
	// This effect runs once when all required data is loaded to prevent infinite loops
	useEffect(() => {
		// Only initialize filters once when all required data is available
		if (!filtersInitialized && portProductData && clinicsData?.items) {
			// Extract product codes from portProductData
			const productCodes = ENABLE_PRODUCT_BY_PORT && portProductData.length
				? portProductData[0].portProducts?.split(',').filter(Boolean) || []
				: [];

			// Build QBO clinic IDs map for non-paid variants
			const qboClinicIds = variant !== 'paid'
				? clinicsData.items.reduce((acc, clinic) => {
					if (clinic.ta_qbocustomerid) {
						acc[`{${clinic.accountid.toLowerCase()}}`] = clinic.ta_qbocustomerid;
					}
					return acc;
				}, {} as Record<string, string>)
				: {};

			setFilters((prevFilters) => ({
				...prevFilters,
				enabled: true,
				globalFilters: JSON.stringify([
					{
						key: CLINIC_KEY,
						values: defaultClinicIds,
					},
					{
						key: PRODUCT_KEY,
						values: productCodes,
					},
				]),
				...(variant !== 'paid' && Object.keys(qboClinicIds).length > 0 && {
					additionalFilters: JSON.stringify([{
						key: QBO_FILTER_KEY,
						values: qboClinicIds,
					}])
				}),
			}));

			setFiltersInitialized(true);
		}
	}, [portProductData, clinicsData, filtersInitialized, defaultClinicIds, ENABLE_PRODUCT_BY_PORT, variant]);

	// Additional state for 'open' variant (multiple invoice viewing)
	const [viewMultipleInvoicesModal, setViewMultipleInvoicesModal] =
		useState<boolean>(false);
	const [invoicesToView, setInvoicesToView] = useState<Invoice[]>([]);
	const [activeInvoiceInList, setActiveInvoiceInList] =
		useState<Invoice | null>(null);

	// Demo modal state (for tutorial)
	const [isDemoInvoiceModalOpen, setIsDemoInvoiceModalOpen] =
		useState<boolean>(false);

	// Determine which invoice to fetch based on active modal (for multi-modal support)
	const invoiceNumberToFetch =
		viewMultipleInvoicesModal && activeInvoiceInList
			? activeInvoiceInList.Invoice
			: undefined;

	const clinicQBOIdToFetch =
		viewMultipleInvoicesModal && activeInvoiceInList
			? clinicsQBOIds[activeInvoiceInList[CLINIC_KEY]?.toLowerCase()] || ''
			: undefined;

	// Use PDF viewer hook with multi-modal support
	const {
		viewInvoiceModal,
		pdfViewerUrl,
		isFetchingPDF,
		isEmptyOrUnavailable,
		isPdfLoadError,
		setViewInvoiceModal,
		setSelectedInvoiceNumber,
		setSelectedClinicQBOId,
		resetPDFViewer,
	} = useInvoicePDFViewer({
		invoiceNumberToFetch,
		clinicQBOIdToFetch,
	});

	// State for selected invoices (open variant only)
	const [selectedInvoiceNumbers, setSelectedInvoiceNumbers] = useState<
		string[]
	>([]);
	const [selectedInvoiceData, setSelectedInvoiceData] = useState<Invoice[]>([]);

	const [invoicesTableRef, invoicesSelectToPayColumnRef] = [
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLTableCellElement | null>(null),
	];

	// Register tutorial actions and handle tutorial logic
	useInvoicesTutorial({
		invoicesTableRef,
		onSelectInvoices: setSelectedInvoiceNumbers,
		onSetSelectedInvoiceData: setSelectedInvoiceData,
		openDemoInvoiceModal: () => {
			setIsDemoInvoiceModalOpen(true);
		},
	});

	// Fetch invoices data based on current filters
	// IMPORTANT: Only disable the query when tutorial is ACTIVE
	// When tutorial is NOT active, enable the query to fetch real data
	const { data, isFetching } = useGetInvoices({
		...filters,
		enabled:
			!isAnyInvoiceTutorialActive && // Enable query when tutorial is NOT active
			filters.enabled && // Wait until filters are set with clinic and product data
			!!portProductData, // Wait until portProductData is fetched
	});

	const isInvoiceQueryEnabled =
		!isAnyInvoiceTutorialActive && filters.enabled && !!portProductData;
	const tableLoading = isAnyInvoiceTutorialActive
		? false
		: isClinicsFetching || !isInvoiceQueryEnabled || isFetching;

	// State and effect to fetch products for product filter options
	const { data: productsData } = useGetInvoicesProducts({
		...filters,
		limit: 9999,
		enabled: filters.enabled && !!portProductData && products.length === 0,
	});

	useEffect(() => {
		if (productsData?.items?.length && !products.length) {
			setProducts(productsData.items);
		}
	}, [products.length, productsData]);

	const [selectColumnElement, setSelectColumnElement] =
		useState<HTMLElement | null>(null);

	// Callback to update select column element when it's mounted (open variant only)
	const handleSelectColumnRefReady = useCallback(
		(element: HTMLElement | null) => {
			if (element && invoicesSelectToPayColumnRef.current !== element) {
				invoicesSelectToPayColumnRef.current = element as HTMLTableCellElement;
			}
			setSelectColumnElement(element);
		},
		[],
	);

	// Register tour for open variant (always call but conditionally use)
	useAppTour({
		elements:
			variant === 'open'
				? {
					invoicesTableRef: invoicesTableRef.current,
					invoicesSelectToPayColumnRef: selectColumnElement,
				}
				: {
					invoicesTableRef: null,
					invoicesSelectToPayColumnRef: null,
				},
	});

	// Memoized table column definitions
	const columns = useMemo(() => user?.portsAvailable?.toLowerCase().includes('woundcare') ? invoiceColumns : invoiceColumns.filter(column => column.key !== 'GrossMargin' && column.key !== 'Invoicecollected'), [user?.portsAvailable]);

	/**
	 * Handle viewing invoice PDF in modal (from table link click)
	 * Sets the invoice number and clinic QBO ID to trigger the PDF fetch
	 */
	const handleViewInvoice = useCallback(
		(invoiceNumber: string, clinicQBOId: string) => {
			if (!clinicQBOId) {
				toast.error('Clinic QBO ID not found for the selected invoice');
				return;
			}

			// Set invoice details to trigger the PDF fetch via useInvoicePDFViewer hook
			setSelectedInvoiceNumber(invoiceNumber);
			setSelectedClinicQBOId(clinicQBOId);
			setViewInvoiceModal(true);
		},
		[setSelectedInvoiceNumber, setSelectedClinicQBOId, setViewInvoiceModal],
	);

	const handleCloseDenialSupportModal = () => {
		setDenialSupportModal((prev) => ({ ...prev, isOpen: false }));
		setIsDenialFormDirty(false);
	};

	/**
	 * Transform raw invoice data for display in the table
	 * - Formats currency values with proper signs and decimals
	 * - Creates expandable sub-rows for invoices with multiple products
	 * - Adds status badges with appropriate colors
	 * - Formats dates and aging information
	 */
	const transformedData = useMemo(() => {
		// IMPORTANT: Only use mock data when the tutorial is ACTIVE
		// When tutorial is NOT active, use real API data
		if (isAnyInvoiceTutorialActive) {
			// Tutorial is active - use mock data
			const mockData = generateMockInvoiceData(10);
			const mockClinicsQBOIds: Record<string, string> = {
				'{61aea445-e3b4-ea11-a816-000d3a191041}': '76',
			};

			return mockData.map((item) => {
				const invoiceStatus = statusInvoices(item)?.toLowerCase();
				const status = getInvoiceStatus(invoiceStatus);
				const invoiceDate = new Date(item.InvoiceDate);

			return {
				subRows: [],
				id: item.InvoiceID,
				Clinic: (
					<FaceliftTruncatedTextWithTooltip
						text={item.Clinic || '--'}
						maxLength={30}
					/>
				),
			Invoice: (
				<Link
					color={'brand.solidSecondary'}
					fontWeight={'bold'}
					onClick={() => {
						handleViewInvoice(
							item.Invoice,
							mockClinicsQBOIds[item[CLINIC_KEY]] || '',
						);
					}}
					maxWidth="150px"
					display="block"
				>
					<FaceliftTruncatedTextWithTooltip
						text={item.Invoice}
						maxLength={20}
						color="brand.solidSecondary"
						fontWeight="bold"
					/>
				</Link>
			),
				State: item.State || '-',
				Product: (
					<FaceliftTruncatedTextWithTooltip
						text={item.Product || '--'}
						maxLength={30}
					/>
				),
				ProductID: (
					<FaceliftTruncatedTextWithTooltip
						text={item.ProductID || '--'}
						maxLength={30}
					/>
				),
				InvoiceDate: formatDateToString(invoiceDate),
				QBAmtDue:
					(item.QBAmtDue || 0) < 0
						? `-$${Math.abs(item.QBAmtDue).toFixed(2)}`
						: `$${item.QBAmtDue.toFixed(2)}`,
				PatientLastName: (
					<FaceliftTruncatedTextWithTooltip
						text={item.PatientLastName || '--'}
						maxLength={25}
					/>
				),
			Invoicecollected:
						typeof item.Invoicecollected === 'number'
							? item.Invoicecollected < 0
								? `-$${Math.abs(item.Invoicecollected).toFixed(2)}`
								: `$${item.Invoicecollected.toFixed(2)}`
							: '--',
					GrossMargin:
						(item.GrossMargin || 0) < 0
							? `-$${Math.abs(item.GrossMargin).toFixed(2)}`
							: `$${item.GrossMargin?.toFixed(2) || '0.00'}`,
					InvoiceStatus: (
						<Box
							bg={['overdue', 'open'].includes(status.label.toLowerCase()) && item.consignment === 'Yes' ? 'teal.500' : status.color}
							borderRadius={4}
							color="white"
							height="24px"
							px={3}
							display="flex"
							alignItems="center"
							justifyContent="center"
							fontWeight={500}
							fontSize="sm"
						>
							<FaceliftTruncatedTextWithTooltip
								text={['overdue', 'open'].includes(status.label.toLowerCase()) && item.consignment === 'Yes' ? 'Consignment' : status.label}
								maxLength={20}
							/>
						</Box>
					),
					Aging: `${item.Aging || 0} days`,
					pendingPayment: item.pendingPayment,
				};
			});
		}

		// Tutorial is NOT active - use real API data
		// Return empty array if data is not available yet
		if (!data?.items || !clinicsData?.items) return [];

		return data.items.map((item) => {
			const invoiceStatus = statusInvoices(item)?.toLowerCase();
			const status = getInvoiceStatus(invoiceStatus);
			const invoiceDate = new Date(item.InvoiceDate);
			const clinicQBOId = clinicsQBOIds[item[CLINIC_KEY]?.toLowerCase()] || '';

			return {
			// Create sub-rows for invoices with multiple products (expandable rows)
			subRows:
				item.subInvoices?.length > 1
					? item.subInvoices?.map((subInvoice) => {
						return {
							id: subInvoice.InvoiceID,
							Clinic: (
								<FaceliftTruncatedTextWithTooltip
									text={subInvoice.Clinic || '--'}
									maxLength={30}
								/>
							),
							Invoice: (
								<FaceliftTruncatedTextWithTooltip
									text={subInvoice.Invoice || '--'}
									maxLength={20}
								/>
							),
							Product: (
								<FaceliftTruncatedTextWithTooltip
									text={subInvoice.Product || '--'}
									maxLength={30}
								/>
							),
							ProductID: (
								<FaceliftTruncatedTextWithTooltip
									text={subInvoice.ProductID || '--'}
									maxLength={30}
								/>
							),
							// Format amount due with proper currency formatting (handle negative values)
							QBAmtDue:
								(subInvoice.QBAmtDue || 0) < 0
									? `-$${Math.abs(subInvoice.QBAmtDue).toFixed(2)}`
									: `$${(subInvoice.QBAmtDue).toFixed(2)}`,
							pendingPayment: subInvoice.pendingPayment,
						};
					})
					: [],
				id: item.InvoiceID,
				Clinic: (
					<FaceliftTruncatedTextWithTooltip
						text={item.Clinic || '-'}
						maxLength={30}
					/>
				),
			Invoice: (
				<Link
					color={'brand.solidSecondary'}
					fontWeight={'bold'}
					onClick={() => {
						handleViewInvoice(item.Invoice, clinicQBOId);
					}}
					maxWidth="150px"
					display="block"
				>
					<FaceliftTruncatedTextWithTooltip
						text={item.Invoice}
						maxLength={20}
						color="brand.solidSecondary"
						fontWeight="bold"
					/>
				</Link>
			),
			State: item.State || '-',
			// Show "Multiple Products" label if invoice has sub-invoices
			Product:
				item.subInvoices.length > 1 ? (
					<FaceliftTruncatedTextWithTooltip
						text="Multiple Products"
						maxLength={30}
					/>
				) : (
					<FaceliftTruncatedTextWithTooltip
						text={item.Product || '-'}
						maxLength={30}
					/>
				),
			ProductID:
				item.subInvoices.length > 1 ? (
					<FaceliftTruncatedTextWithTooltip
						text="Multiple Tissue IDs"
						maxLength={30}
					/>
				) : (
					<FaceliftTruncatedTextWithTooltip
						text={item.ProductID || '-'}
						maxLength={30}
					/>
				),
			InvoiceDate: formatDateToString(invoiceDate),
			// Format QuickBooks amount due with proper currency formatting
			QBAmtDue:
				(item.QBAmtDue || 0) < 0
					? `-$${Math.abs(item.QBAmtDue).toFixed(2)}`
					: `$${item.QBAmtDue.toFixed(2)}`,
			PatientLastName: (
				<FaceliftTruncatedTextWithTooltip
					text={item.PatientLastName || '--'}
					maxLength={25}
				/>
			),
			// Format invoice collected amount (handle null/undefined values)
			Invoicecollected:
					typeof item.Invoicecollected === 'number'
						? item.Invoicecollected < 0
							? `-$${Math.abs(item.Invoicecollected).toFixed(2)}`
							: `$${item.Invoicecollected.toFixed(2)}`
						: '--',
				// Format gross margin with proper currency formatting
				GrossMargin:
					(item.GrossMargin || 0) < 0
						? `-$${Math.abs(item.GrossMargin).toFixed(2)}`
						: `$${item.GrossMargin?.toFixed(2) || '0.00'}`,
				// Render status badge with dynamic color based on invoice status
				InvoiceStatus: (
					<Box
						bg={['overdue', 'open'].includes(status.label.toLowerCase()) && item.consignment === 'Yes' ? 'teal.500' : status.color}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						<FaceliftTruncatedTextWithTooltip
							text={['overdue', 'open'].includes(status.label.toLowerCase()) && item.consignment === 'Yes' ? 'Consignment' : status.label}
							maxLength={20}
						/>
					</Box>
				),
				Aging: `${item.Aging || 0} days`,
				pendingPayment: item.pendingPayment,
				clinicQBOId: clinicQBOId,
			};
		});
	}, [
		data,
		clinicsData,
		clinicsQBOIds,
		handleViewInvoice,
		isAnyInvoiceTutorialActive,
	]);


	// Force registration of checkbox column refs when mock data is mounted
	const { registerElement } = useTourContext();
	useEffect(() => {
		if (!isAnyInvoiceTutorialActive || !transformedData.length) {
			return;
		}

		// Wait for table to be rendered with mock data
		const registerCheckboxRefs = () => {
			if (!invoicesTableRef.current) return false;

			const tableRoot = invoicesTableRef.current;
			const thead = tableRoot.querySelector('thead');
			const tbody = tableRoot.querySelector('tbody');

			if (!thead || !tbody || tbody.querySelectorAll('tr').length === 0) {
				return false;
			}

			const headers = thead.querySelectorAll('th');
			const firstHeader = headers[0];
			if (!firstHeader) return false;

			const table = thead.closest('table');
			if (!table) return false;

			const columnIndex = 0;
			const allRows = table.querySelectorAll('tr');
			if (allRows.length === 0) return false;

			// Only include data rows (skip header row)
			const dataCellsInColumn: HTMLElement[] = [];

			// Start from row 1 (skip header row at index 0)
			for (let i = 1; i < allRows.length; i++) {
				const row = allRows[i];
				const cell = row.children[columnIndex] as HTMLElement;
				if (cell) {
					const cellRect = cell.getBoundingClientRect();
					if (cellRect.height > 0 && cellRect.width > 0) {
						dataCellsInColumn.push(cell);
					}
				}
			}

			if (dataCellsInColumn.length === 0) return false;

			const firstCellRect = dataCellsInColumn[0].getBoundingClientRect();
			const lastCellRect =
				dataCellsInColumn[dataCellsInColumn.length - 1].getBoundingClientRect();

			// Calculate height: from top of first data cell to bottom of last data cell
			// This ensures we only highlight the checkbox cells, not the header
			const totalHeight = lastCellRect.bottom - firstCellRect.top;

			// Clean up any existing wrapper
			const existingWrappers = document.querySelectorAll(
				'[data-tour-column-wrapper="true"]',
			);
			existingWrappers.forEach((wrapper) => {
				const el = wrapper as HTMLElement & { __cleanup?: () => void };
				if (el.__cleanup) {
					el.__cleanup();
				}
				el.remove();
			});

			const wrapper = document.createElement('div');
			wrapper.style.position = 'fixed';
			wrapper.style.left = `${firstCellRect.left}px`;
			wrapper.style.top = `${firstCellRect.top}px`;
			wrapper.style.width = `${firstCellRect.width}px`;
			wrapper.style.height = `${totalHeight}px`;
			wrapper.style.maxHeight = `${totalHeight}px`;
			wrapper.style.pointerEvents = 'none';
			wrapper.style.zIndex = '9998';
			wrapper.style.boxSizing = 'border-box';
			wrapper.style.overflow = 'hidden';
			wrapper.setAttribute('data-tour-column-wrapper', 'true');
			wrapper.setAttribute('data-column-index', String(columnIndex));

			document.body.appendChild(wrapper);

			// Set up scroll listener to update position
			let rafId: number | null = null;
			const updatePosition = () => {
				if (rafId !== null) {
					cancelAnimationFrame(rafId);
				}
				rafId = requestAnimationFrame(() => {
					if (!invoicesTableRef.current) return;

					const tableRoot = invoicesTableRef.current;
					const table = tableRoot.querySelector('table');
					if (!table) return;

					const allRows = table.querySelectorAll('tr');
					if (allRows.length === 0) return;

					const dataCellsInColumn: HTMLElement[] = [];
					for (let i = 1; i < allRows.length; i++) {
						const row = allRows[i];
						const cell = row.children[columnIndex] as HTMLElement;
						if (cell) {
							const cellRect = cell.getBoundingClientRect();
							if (cellRect.height > 0 && cellRect.width > 0) {
								dataCellsInColumn.push(cell);
							}
						}
					}

					if (dataCellsInColumn.length === 0) return;

					const firstCellRect = dataCellsInColumn[0].getBoundingClientRect();
					const lastCellRect =
						dataCellsInColumn[
							dataCellsInColumn.length - 1
						].getBoundingClientRect();
					const totalHeight = lastCellRect.bottom - firstCellRect.top;

					wrapper.style.left = `${firstCellRect.left}px`;
					wrapper.style.top = `${firstCellRect.top}px`;
					wrapper.style.width = `${firstCellRect.width}px`;
					wrapper.style.height = `${totalHeight}px`;
					wrapper.style.maxHeight = `${totalHeight}px`;
				});
			};

			updatePosition();
			window.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);

			const tableElement = tableRoot.querySelector('table');
			const tableContainer =
				tableElement?.closest('[data-id="smart-data-table"]') ||
				tableElement?.closest('.chakra-table__container') ||
				tableElement?.parentElement;
			if (tableContainer) {
				tableContainer.addEventListener('scroll', updatePosition, true);
			}

			// Store cleanup function
			(wrapper as HTMLElement & { __cleanup?: () => void }).__cleanup = () => {
				if (rafId !== null) {
					cancelAnimationFrame(rafId);
				}
				window.removeEventListener('scroll', updatePosition, true);
				window.removeEventListener('resize', updatePosition);
				if (tableContainer) {
					tableContainer.removeEventListener('scroll', updatePosition, true);
				}
			};

			// Register both references immediately
			registerElement('InvoicesCheckboxColumnRef', wrapper);
			registerElement('InvoicesCheckboxColumnRef2', wrapper);

			return true;
		};

		// Try immediately and with delays to ensure table is rendered
		const registered = registerCheckboxRefs();
		if (registered) return;

		const timeoutId1 = setTimeout(() => {
			if (isAnyInvoiceTutorialActive) registerCheckboxRefs();
		}, 100);
		const timeoutId2 = setTimeout(() => {
			if (isAnyInvoiceTutorialActive) registerCheckboxRefs();
		}, 300);
		const timeoutId3 = setTimeout(() => {
			if (isAnyInvoiceTutorialActive) registerCheckboxRefs();
		}, 500);

		return () => {
			clearTimeout(timeoutId1);
			clearTimeout(timeoutId2);
			clearTimeout(timeoutId3);
		};
	}, [
		isAnyInvoiceTutorialActive,
		transformedData.length,
		invoicesTableRef,
		registerElement,
	]);

	/**
	 * Transform invoice data for export (CSV/Excel/PDF)
	 * Similar to transformedData but with plain text instead of JSX elements
	 * Sub-rows have empty parent columns to maintain proper formatting
	 */
	const transformedDataToExport = (invoiceData: Invoice[]) => {
		if (!invoiceData.length) return [];

		return invoiceData.map((item) => {
			const invoiceStatus = statusInvoices(item)?.toLowerCase();
			const status = getInvoiceStatus(invoiceStatus);
			const invoiceDate = new Date(item.InvoiceDate);

			return {
				subRows:
					item.subInvoices?.length > 1
						? item.subInvoices?.map((subInvoice) => {
							return {
								id: '', // Add the required 'id' property
								Clinic: '',
								Invoice: '',
								Product: subInvoice.Product,
								ProductID: subInvoice.ProductID,
								QBAmtDue:
									(subInvoice.QBAmtDue || 0) < 0
										? `-$${Math.abs(subInvoice.QBAmtDue).toFixed(2)}`
										: `$${(subInvoice.QBAmtDue).toFixed(2)}`,
								pendingPayment: '',
								InvoiceDate: '',
								PatientLastName: '',
								Invoicecollected: '',
								GrossMargin: '',
								InvoiceStatus: '',
								Aging: '',
							};
						})
						: [],
				id: item.InvoiceID,
				Clinic: item.Clinic,
				Invoice: item.Invoice,
				State: item.State || '-',
				Product:
					item.subInvoices.length > 1 ? 'Multiple Products' : item.Product,
				ProductID:
					item.subInvoices.length > 1 ? 'Multiple Tissue IDs' : item.ProductID,
				InvoiceDate: formatDateToString(invoiceDate),
				QBAmtDue:
					(item.QBAmtDue || 0) < 0
						? `-$${Math.abs(item.QBAmtDue).toFixed(2)}`
						: `$${item.QBAmtDue.toFixed(2)}`,
				PatientLastName: item.PatientLastName || '--',
				Invoicecollected:
					typeof item.Invoicecollected === 'number'
						? item.Invoicecollected < 0
							? `-$${Math.abs(item.Invoicecollected).toFixed(2)}`
							: `$${item.Invoicecollected.toFixed(2)}`
						: '--',
				GrossMargin:
					(item.GrossMargin || 0) < 0
						? `-$${Math.abs(item.GrossMargin).toFixed(2)}`
						: `$${item.GrossMargin?.toFixed(2) || '0.00'}`,
				// Use plain text status label instead of JSX for export
				InvoiceStatus: status.label,
				Aging: `${item.Aging || 0} days`,
				pendingPayment: item.pendingPayment,
			};
		});
	};

	/**
	 * Handle filter changes from the DataTable component
	 * Transforms filter parameters into the format expected by the API
	 * - Converts status filter (overdue becomes past_due with current date)
	 * - Maps global filter names to API keys (Clinics -> Account_ID, Products -> QCode)
	 * - Serializes complex filter objects to JSON strings
	 *
	 * good to pay: gross_margin_value: positive
	 */
	const onFilterApply = useCallback(
		({
			page,
			limit,
			status,
			search = '',
			sort = null,
			columnsFilters = [],
		}: onFilterApplyProps) => {
			const statusFilter =
				variant === 'paid'
					? 'paid'
					: status?.value === 'overdue'
						? `past_due,${getCurrentDate()}`
						: status?.value || '';

			const productCodes = ENABLE_PRODUCT_BY_PORT && portProductData?.length
				? portProductData[0].portProducts?.split(',').filter(Boolean) || []
				: [];

			// Build QBO clinic IDs map for non-paid variants
			const qboClinicIds = variant !== 'paid'
				? clinicsData?.items.reduce((acc, clinic) => {
					if (clinic.ta_qbocustomerid) {
						acc[`{${clinic.accountid.toLowerCase()}}`] = clinic.ta_qbocustomerid;
					}
					return acc;
				}, {} as Record<string, string>)
				: {};

			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				search,
				enabled: defaultClinicIds.length > 0,
				tab: variant, // Identifies the table mode (all/open/paid) for audit logging
				// Convert 'overdue' status to API format: 'past_due,YYYY-MM-DD'
				status: statusFilter,

				globalFilters: JSON.stringify([
					{
						key: CLINIC_KEY,
						values: defaultClinicIds,
					},
					{
						key: PRODUCT_KEY,
						values: productCodes,
					},
				]),
				...(variant !== 'paid' && qboClinicIds && Object.keys(qboClinicIds).length > 0 && {
					additionalFilters: JSON.stringify([{
						key: QBO_FILTER_KEY,
						values: qboClinicIds,
					}])
				}),
				sort: sort ? `${sort.key}|${sort.direction}` : '',
			});
		},
		[mapGlobalFilters, portProductData, user?.portsAvailable, variant],
	);

	/**
	 * Fetch all invoice data for export (CSV/Excel/PDF)
	 *
	 * Since the table is paginated, this function fetches all pages of data
	 * with the current filters applied to ensure the export contains complete data
	 *
	 * @returns Transformed invoice data ready for export
	 */
	const fetchAllDataToExport = async () => {
		let allItems: Invoice[] = [];
		let page = 1;
		let hasMore = true;

		// Fetch all pages of data with current filters
		while (hasMore) {
			const response = await getInvoices(
				page,
				9999, // Large page size to fetch all data
				filters.tab || '',
				filters.columnsFilters,
				filters.search,
				filters.status as string,
				filters.globalFilters,
				filters.sort || '',
				'',
				filters.additionalFilters || '',
			);

			if (response?.items?.length) {
				allItems = allItems.concat(response.items);
				const totalItems = response?.meta?.totalItems || 0;
				hasMore = allItems.length < totalItems;
				page += 1;
			} else {
				hasMore = false;
			}
		}

		const invoiceDataForExcel = { items: allItems };

		// Transform the data for export (removes JSX, formats for spreadsheet)
		const transformedData = transformedDataToExport(
			invoiceDataForExcel.items || [],
		);

		return transformedData;
	};

	/**
	 * Export all filtered invoice data as CSV
	 * Fetches all pages with current filters and generates CSV file
	 */
	const exportAllAsCSV = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateCSVFromTable(
				columns,
				transformedInvoiceData,
				`Invoice${variant ? `-${variant}` : ''}`,
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	/**
	 * Export all filtered invoice data as Excel
	 * Fetches all pages with current filters and generates Excel file
	 */
	const exportAllAsExcel = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateExcelFromTable(
				columns,
				transformedInvoiceData,
				`Invoice${variant ? `-${variant}` : ''}`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	/**
	 * Export all filtered invoice data as PDF
	 * TODO: Implement PDF export logic
	 */
	const exportAllAsPDF = useCallback(() => {
		// Implement PDF export logic here
	}, []);

	/**
	 * Handle viewing multiple selected invoices in split-view modal (open variant only)
	 * Opens dialog with list of invoices on left and PDF viewer on right
	 */
	const handleViewMultipleInvoices = useCallback(() => {
		const invoiceData = selectedInvoiceData.length
			? selectedInvoiceData
			: cartedInvoices;
		if (invoiceData.length === 0) {
			toast.error('Please select at least one invoice to view');
			return;
		}

		// Set invoices to view and open the first one by default
		// Set the active invoice first so it's available when modal opens
		setInvoicesToView(invoiceData);
		setActiveInvoiceInList(invoiceData[0]);
		
		// Open modal last - this will trigger the PDF fetch via invoiceNumberToFetch/clinicQBOIdToFetch
		setViewMultipleInvoicesModal(true);
	}, [selectedInvoiceData, cartedInvoices]);

	/**
	 * Handle clicking an invoice in the list to view its PDF (open variant only)
	 */
	const handleInvoiceClick = useCallback(
		(invoice: Invoice) => {
			// Reset PDF when switching invoices
			resetPDFViewer();
			setActiveInvoiceInList(invoice);
		},
		[resetPDFViewer],
	);

	/**
	 * Handle filter changes from the DataTable component (open variant specific)
	 * Supports user-selected global filters (clinics, products) from the UI
	 */
	const onFilterApplyOpen = useCallback(
		({
			page,
			limit,
			status,
			search = '',
			sort = null,
			columnsFilters = [],
			globalFilters = [],
		}: onFilterApplyProps) => {
			// Map global filters from UI (handles clinic/product selections)
			const mappedFilters = mapGlobalFilters(globalFilters || []);

			// Extract clinic IDs from mapped filters or fall back to default
			const selectedClinicIds = mappedFilters.find((f) => f.key === CLINIC_KEY)?.values || defaultClinicIds;

			// Extract product codes: prioritize user selection, then portProductData, then empty
			const selectedProductCodes = mappedFilters.find((f) => f.key === PRODUCT_KEY)?.values || 
				(ENABLE_PRODUCT_BY_PORT && portProductData?.length
					? portProductData[0].portProducts?.split(',').filter(Boolean) || []
					: []);

			// Build QBO clinic IDs map for non-paid variants
			const qboClinicIds = variant !== 'paid' && clinicsData?.items
				? clinicsData.items.reduce((acc, clinic) => {
						if (clinic.ta_qbocustomerid) {
							acc[`{${clinic.accountid.toLowerCase()}}`] = clinic.ta_qbocustomerid;
						}
						return acc;
					}, {} as Record<string, string>)
				: {};

			// Convert status to API format
			const apiStatus = status?.value === 'overdue'
				? `past_due,${getCurrentDate()}`
				: status?.value || statusesFilterOptionsForOpen[0].value;

			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				search,
				enabled: defaultClinicIds.length > 0,
				tab: variant, // Identifies the table mode (all/open/paid) for audit logging
				status: apiStatus,
				globalFilters: JSON.stringify([
					{
						key: CLINIC_KEY,
						values: selectedClinicIds,
					},
					{
						key: PRODUCT_KEY,
						values: selectedProductCodes,
					},
				]),
				...(variant !== 'paid' && Object.keys(qboClinicIds).length > 0 && {
					additionalFilters: JSON.stringify([{
						key: QBO_FILTER_KEY,
						values: qboClinicIds,
					}])
				}),
				sort: sort ? `${sort.key}|${sort.direction}` : '',
			});
		},
		[
			mapGlobalFilters,
			defaultClinicIds,
			ENABLE_PRODUCT_BY_PORT,
			portProductData,
			variant,
			clinicsData,
		],
	);

	/**
	 * Handle row selection changes in the table (open variant only)
	 * - Removes deselected invoices from cart
	 * - Updates selected invoice numbers and data
	 */
	const onSelection = (ids: string[]) => {
		// Find and remove invoices that were deselected
		selectedInvoiceNumbers
			.filter((id) => !ids.includes(id))
			.forEach((id) => {
				const invoice = selectedInvoices.find(
					(inv) => String(inv.InvoiceID) === id,
				);
				if (invoice) {
					removeInvoiceFromCart(id);
				}
			});

		// Update selected invoice data from current page items
		// Use mock data if tutorial is active, otherwise use real data
		const currentData = isAnyInvoiceTutorialActive
			? generateMockInvoiceData(10)
			: data?.items || [];
		const selectedData = currentData
			.filter((item) => ids.includes(String(item.InvoiceID)))
			.map((item) => ({
				...item,
				clinicQBOId: clinicsQBOIds[item[CLINIC_KEY]?.toLowerCase()] || '',
			}));
		setSelectedInvoiceData(selectedData || []);

		// Update selected invoice IDs
		setSelectedInvoiceNumbers(ids);
	};

	/**
	 * Add selected invoices to the shopping cart (open variant only)
	 * Only adds invoices that aren't already in the cart or paid on QBO
	 */
	const addInvoicesToCart = () => {
		const requestedInvoices = isAnyInvoiceTutorialActive
			? selectedInvoiceData
			: (selectedInvoiceNumbers
					.map((id) =>
						data?.items?.find((item) => String(item.InvoiceID) === id),
					)
					.filter(Boolean) as Invoice[]);

		trackCoreClick('invoice_add_to_cart_clicked', {
			source_module: 'invoices_table',
			table_variant: variant,
			invoice_count: requestedInvoices.length,
			invoice_numbers: requestedInvoices.map((invoice) => invoice.Invoice),
			invoice_ids: requestedInvoices.map((invoice) => invoice.InvoiceID),
		});

		const invoicesToAdd: Invoice[] = [];
		const paidOnQBOInvoices: Invoice[] = [];

		if (isAnyInvoiceTutorialActive && selectedInvoiceData.length > 0) {
			// Tutorial mode: use selectedInvoiceData
			const newInvoices = selectedInvoiceData.filter(
				(invoice) =>
					!selectedInvoices.some(
						(inv) => inv.InvoiceID === invoice.InvoiceID,
					),
			);

			// Separate paid on QBO invoices from valid ones
			newInvoices.forEach((invoice) => {
				if (invoice.isPaidOnQBO) {
					paidOnQBOInvoices.push(invoice);
				} else {
					invoicesToAdd.push({
						...invoice,
						clinicQBOId: clinicsQBOIds[invoice[CLINIC_KEY]] || '',
					});
				}
			});
		} else {
			// Normal mode: use selectedInvoiceNumbers
			const newInvoiceIds = selectedInvoiceNumbers.filter(
				(id) => !selectedInvoices.some((invoice) => invoice.InvoiceID === id),
			);

			newInvoiceIds.forEach((id) => {
				const invoice = data?.items?.find(
					(item) => String(item.InvoiceID) === id,
				);

				if (invoice) {
					if (invoice.isPaidOnQBO) {
						paidOnQBOInvoices.push(invoice);
					} else {
						invoicesToAdd.push({
							...invoice,
							clinicQBOId: clinicsQBOIds[invoice[CLINIC_KEY]?.toLowerCase()] || '',
						});
					}
				}
			});
		}

		// Show info for paid on QBO invoices
		if (paidOnQBOInvoices.length > 0) {
			// Show toast message for paid invoices
			const invoiceNumbers = paidOnQBOInvoices.map((inv) => inv.Invoice).join(', ');
			const message = paidOnQBOInvoices.length === 1
				? `Invoice #${invoiceNumbers} is already paid in the system. Please contact Administrator`
				: `Invoices #${invoiceNumbers} are already paid in the system. Please contact Administrator`;
			toast(message, { icon: '⚠️' });
		}

		// Add valid invoices to cart
		if (invoicesToAdd.length > 0) {
			invoicesToAdd.forEach((invoice) => {
				addInvoiceToCart(invoice);
			});
			const messgae = invoicesToAdd.length === 1
				? `Invoice ${invoicesToAdd[0].Invoice} added to the cart`
				: `Invoices ${invoicesToAdd.map(inv => inv.Invoice).join(', ')} added to the cart`;
			toast.success(messgae);
		}
	};

	// We will only show Open, Overdue and Paid options in All Invoices page
	const statusOptions = useMemo(
		() =>
			!variant || variant === 'all'
				? statusesFilterOptions
				: user?.portsAvailable?.toLowerCase().includes('woundcare') ? statusesFilterOptionsForOpen : [],
		[],
	);

	// Memoize globalActions so DataTable/Filters don't re-render on every parent render
	// (avoids dropdown re-mounting and "2-3 clicks to open" issue).
	const globalActions = useMemo(() => {
		const actions = [
			...(isAnyInvoiceTutorialActive && selectedInvoiceData.length > 0
				? [
					{
						title: 'Add to Cart',
						action: () => {
							addInvoicesToCart();
						},
					},
				]
				: selectedInvoiceData.some((invoice) => {
					const invoiceStatusData = getInvoiceStatus(
						statusInvoices(invoice)?.toLowerCase(),
					);
					return ['paid', 'pending', 'processing'].includes(
						invoiceStatusData.label.toLowerCase(),
					);
				}) ||
					(cartedInvoices.length > 0 &&
						selectedInvoiceData.filter((invoice) =>
							cartedInvoices.some(
								(inv) => inv.InvoiceID === invoice.InvoiceID,
							),
						).length === selectedInvoiceData.length) ||
					selectedInvoiceData.every((invoice) => invoice.QBAmtDue === 0) ||
					(filters.status as string)?.toLowerCase() === 'paid' ||
					(filters.status as string)?.toLowerCase() === 'processing' ||
					!hasPermission(['Invoice:Pay'])
					? []
					: [
						{
							title: 'Add to Cart',
							action: () => {
								addInvoicesToCart();
							},
						},
					]),
			...(selectedInvoiceData.length === 1 &&
				!selectedInvoiceData[0].pendingPayment
				? [
					{
						title: 'Denial Support',
						action: () => {
							setDenialSupportModal({
								isOpen: true,
								isErrorOpen: false,
								message: '',
							});
						},
					},
				]
				: []),
			{
				title: `View Invoice(s)`,
				action: handleViewMultipleInvoices,
			},
		];
		return actions;
	}, [
		selectedInvoiceData,
		isAnyInvoiceTutorialActive,
		cartedInvoices,
		filters.status,
		hasPermission,
		addInvoicesToCart,
		handleViewMultipleInvoices,
	]);

	const tableLabel = !variant
		? 'Invoices'
		: variant === 'paid'
			? 'Paid Invoices'
			: variant === 'open'
				? 'Open Invoices'
				: 'All Invoices';

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			borderRadius="md"
			boxShadow="sm"
		>
			{/* This Dialog is used to view a single invoice PDF (from table link click) */}
			<FaceliftInvoiceViewPDF
				viewInvoiceModal={viewInvoiceModal}
				isLoading={isFetchingPDF}
				pdfViewerUrl={pdfViewerUrl}
				isEmptyOrUnavailable={isEmptyOrUnavailable}
				isLoadError={isPdfLoadError}
				onOpenChange={() => {
					setViewInvoiceModal(!viewInvoiceModal);
					// Clear state when modal closes
					if (!viewInvoiceModal) {
						// Reset previous PDF
						resetPDFViewer();
					}
				}}
			/>

			<FaceliftDialogWithExitConfirmation
				showBackBtn={true}
				open={denialSupportModal.isOpen}
				onOpenChange={(open) => {
					if (!open) handleCloseDenialSupportModal();
				}}
				onInteractOutside={(e: { detail?: { originalEvent?: { target?: EventTarget | null } }; preventDefault: () => void }) => {
					const target = e?.detail?.originalEvent
						?.target as HTMLElement | undefined;
					if (target?.closest('[data-facelift-date-picker-portal]')) {
						e.preventDefault();
					}
				}}
				title={'Create New Denial Support Request'}
				size="xl"
				sx={{
					width: '850px',
					maxWidth: '95vw',
					maxHeight: '95vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'hidden',
					overflowY: 'auto',
				}}
				isDirty={isDenialFormDirty}
			>
				<DenialSupportForm
					onSuccess={() => {
						if (denialSupportModal.isOpen) handleCloseDenialSupportModal();
					}}
					data={{
						invoiceNumber: selectedInvoiceData[0]?.Invoice || '',
						clinicId: selectedInvoiceData[0]?.Account_ID || '',
						totalProducts: selectedInvoiceData[0]?.subInvoices?.length || 1,
					}}
					onError={(message) => {
						setDenialSupportModal({
							isOpen: false,
							isErrorOpen: true,
							message,
						});
						if (!denialSupportModal.isOpen) handleCloseDenialSupportModal();
					}}
					onIsDirtyChange={setIsDenialFormDirty}
				/>
			</FaceliftDialogWithExitConfirmation>

			<FaceliftMessage
				message={denialSupportModal.message}
				error={true}
				messageTitle="Error Loading Order Details"
				messageSx={{
					height: 'auto',
					minHeight: '150px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
				onClose={() =>
					setDenialSupportModal({
						isOpen: false,
						isErrorOpen: false,
						message: '',
					})
				}
			/>

			{/* This Dialog is used to view multiple selected invoices with list on left and PDF on right (open and all variants) */}
			{['open', 'all'].includes(variant) && (
				<FaceliftDialog
					showBackBtn={true}
					open={viewMultipleInvoicesModal}
					onOpenChange={(isOpen) => {
						setViewMultipleInvoicesModal(isOpen);
						if (!isOpen) {
							setInvoicesToView([]);
							setActiveInvoiceInList(null);
							resetPDFViewer();
						}
					}}
					title={'View Invoices'}
					size="xl"
					sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
				>
					<InvoiceSplitView
						invoices={invoicesToView}
						activeInvoice={activeInvoiceInList || invoicesToView[0]}
						getStatus={(invoice) =>
							getInvoiceStatus(statusInvoices(invoice)?.toLowerCase())
						}
						onInvoiceClick={handleInvoiceClick}
						isLoadingPDF={isFetchingPDF}
						pdfUrl={pdfViewerUrl}
						isEmptyOrUnavailable={isEmptyOrUnavailable}
						isLoadError={isPdfLoadError}
					/>
				</FaceliftDialog>
			)}
			{/* Demo Invoice View Modal for Tutorial */}
			<FaceliftDialog
				open={isDemoInvoiceModalOpen}
				showBackBtn={true}
				onOpenChange={(openState) => {
					// Keep modal open during tutorial unless explicitly closed
					if (!openState && isAnyInvoiceTutorialActive) {
						return;
					}
					setIsDemoInvoiceModalOpen(openState);
				}}
				title="View Invoice"
				size="xl"
				sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
			>
				<DemoInvoiceViewModal />
			</FaceliftDialog>
			{['open', 'all'].includes(variant) ? (
				<DataTable
					tableName={tableLabel}
					ref={invoicesTableRef}
					moduleName="Invoices"
					loading={tableLoading}
					items={transformedData}
					columns={columns}
					topLabel={tableLabel}
					totalCount={
						isAnyInvoiceTutorialActive ? 10 : data?.meta.totalItems || 0
					}
					onFilterApply={onFilterApplyOpen}
					onSelection={onSelection}
					requiredColumnsForExpand={['Product', 'ProductID']}
					bordered
					searchable
					hideShowColumn
					expandable
					paginated
					fullscreen
					globalFilter={{
						title: 'Filters',
						clinics: true,
						items: [
							{
								options: products.map((product) => ({
									label: product.productName,
									id: product.qCode,
								})),
								key: 'QCode',
								name: 'Products',
							},
						],
					}}
					exportable
					csv
					excel
					customExportItemsData={transformedDataToExport(data?.items || [])}
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
					exportAllAsPDF={exportAllAsPDF}
					isSelectable
					selectedItems={selectedInvoices.map((invoice) => invoice.InvoiceID)}
					globalActions={globalActions}
					statusFilters={statusOptions}
					hideAllStatusFilter
					selectColumnRef={invoicesSelectToPayColumnRef}
					onSelectColumnRefReady={handleSelectColumnRefReady}
				/>
			) : (
				<DataTable
					tableName={tableLabel}
					moduleName="Invoices"
					loading={tableLoading}
					items={transformedData}
					columns={columns}
					topLabel={tableLabel}
					totalCount={
						isAnyInvoiceTutorialActive ? 10 : data?.meta.totalItems || 0
					}
					onFilterApply={onFilterApply}
					requiredColumnsForExpand={['Product', 'ProductID']}
					bordered
					searchable
					hideShowColumn
					expandable
					paginated
					fullscreen
					customExportItemsData={transformedDataToExport(data?.items || [])}
					globalFilter={{
						title: 'Filters',
						clinics: true,
						items: [
							{
								options: products.map((product) => ({
									label: product.productName,
									id: product.qCode,
								})),
								key: 'QCode',
								name: 'Products',
							},
						],
					}}
					exportable
					csv
					excel
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
					exportAllAsPDF={exportAllAsPDF}
				/>
			)}
		</Box>
	);
};

export default InvoicesUnifiedTable;
